import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { oneLine } from 'common-tags';

import { Button, Hamburger } from '@/atoms';
import { ButtonSize, ButtonVariant } from '@/atoms/button/types';

import LogoBlack from '@/assets/images/logodark.png';
import { useFixPortalToBody, useRouteChangeStart } from '@/hooks';
import { routes } from '@/lib';
import { NavItem } from '../nav-item/NavItem';

import type { FunctionComponent } from 'react';
import type { TypeMobileNavProps } from './types';

/**
 * MobileNav
 */
export const MobileNav: FunctionComponent<TypeMobileNavProps> = ({
  menu,
  socials,
  theme,
  open,
  setOpen,
  topBar,
  hideTrigger = false,
}: TypeMobileNavProps) => {
  const slug = usePathname();

  const social = socials?.networks.map((ele, index: number) => (
    <Link key={index} href={ele.href} legacyBehavior prefetch={false}>
      <a className="text-text-neutrals-900">
        <ele.Icon width={24} />
      </a>
    </Link>
  ));

  const initialNavItemsState = new Array(menu.length).fill(false);
  const [navItemStates, setNavItemStates] = useState(initialNavItemsState);

  const getUpdatedNavItemsStates = (navItemIndex: number) => {
    const navItemsStatesClone = [...navItemStates];
    navItemsStatesClone[navItemIndex] = !navItemsStatesClone[navItemIndex];

    return navItemsStatesClone;
  };

  useRouteChangeStart(() => {
    setOpen(false);
    setNavItemStates(initialNavItemsState);
  });
  useFixPortalToBody(open);

  return (
    <nav
      // En el layout `centered` (hideTrigger) el trigger vive en el grid del
      // Header y este <nav> sólo envuelve el drawer `fixed`; con `h-full` el
      // wrapper desborda ~72px bajo el header (overflow visible) y, dentro del
      // stacking del header (z-40), intercepta los clicks de lo que haya justo
      // debajo (p.ej. la barra de filtros del listado). `h-0` lo neutraliza sin
      // afectar al drawer (que es `fixed`).
      className={oneLine`flex w-full justify-end lg:hidden ${
        hideTrigger ? 'h-0' : 'h-full'
      }`}
      role="navigation"
      aria-label="Menú principal"
    >
      {!hideTrigger && (
        <div
          className={oneLine`bg-neutrals-0 relative z-50 flex w-full flex-row items-center justify-end px-4 ${open ? 'shadow-light-xs' : ''} `}
        >
          <Link href={routes.index} className={open ? '' : 'hidden'}>
            <Image
              width={LogoBlack.width}
              height={LogoBlack.height}
              src={LogoBlack.src}
              alt="Logo de la web"
            />
          </Link>

          <Hamburger
            theme={theme}
            isOpen={open}
            onClick={() => setOpen(!open)}
            className={open ? 'z-30 ml-auto' : ''}
            aria-expanded={open}
            aria-controls="main-menu"
            aria-label="Abrir menú principal"
          />
        </div>
      )}

      <div
        // `fixed inset-0` para que el drawer cubra el viewport. El padre
        // posicionado más cercano sería el `<header>` (height = header-height),
        // así que con `absolute` el drawer quedaría recortado a 72px.
        className={oneLine`bg-bg-neutrals-0 fixed inset-0 z-20 transform overflow-x-hidden overflow-y-auto ${open ? 'translate-x-0' : '-translate-x-full'} drop-shadow-lg filter transition-transform duration-300 ease-in-out`}
      >
        <div className="mx-4 flex h-full flex-col gap-10">
          <ul className="pt-[calc(16px+var(--header-height))]" id="main-menu">
            {menu.map((ele, index: number) => {
              const isActive = (href: string | undefined) =>
                href
                  ? href === '/'
                    ? href === slug
                    : slug.includes(href)
                  : false;

              return (
                <li
                  key={index}
                  {...(ele.children &&
                    !ele.href && {
                      onClick: () =>
                        setNavItemStates(getUpdatedNavItemsStates(index)),
                    })}
                >
                  <NavItem
                    label={ele.label}
                    href={ele.href}
                    theme={theme}
                    isActive={isActive(ele.href)}
                    className="border-bg-neutrals-50 border-b px-2 py-6"
                    isOpen={navItemStates[index]}
                    {...(ele.children &&
                      ele.href && {
                        onClickIcon: () =>
                          setNavItemStates(getUpdatedNavItemsStates(index)),
                      })}
                  >
                    {ele.children && (
                      <div
                        className={oneLine` ${navItemStates[index] ? 'flex' : 'hidden'} text-text-primary lg:group-hover:pt-header inset-0 bottom-20 w-full bg-white lg:static lg:top-0 lg:group-hover:absolute lg:group-hover:right-0 lg:group-hover:left-0 lg:group-hover:-z-10 lg:group-hover:flex lg:group-hover:justify-center`}
                        data-submenu-open={navItemStates[index]}
                      >
                        <ele.children.type
                          {...(ele.children.props as Record<string, unknown>)}
                          isActive={isActive}
                        />
                      </div>
                    )}
                  </NavItem>
                </li>
              );
            })}
          </ul>

          {topBar && (
            <div className="mt-auto pb-4">
              <div className="rounded-custom bg-primary-50 flex flex-col p-4">
                <div className="mb-4 flex flex-col gap-2">
                  <span className="text-body-l font-bold">
                    Dinos cómo quieres vivir
                  </span>

                  <span className="text-body-m">
                    ¿Buscas una vivienda para unos meses o para más de un año?
                  </span>
                </div>

                <div className="flex flex-col gap-2">
                  {topBar.map((child, index) => (
                    <Button
                      key={index}
                      as="a"
                      href={child.url}
                      variant={ButtonVariant.SECONDARY}
                      size={ButtonSize.SMALL}
                    >
                      {child.title}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {socials && (
            <div className="text-text-blue-900 flex items-center justify-between pb-4">
              <div className="text-body text-body-l font-bold">
                {socials.title}
              </div>
              <div className="flex gap-1">{social}</div>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

MobileNav.displayName = 'MobileNav';
